import { Text } from '#app/components/Primitives/Text.js';
import { ProBadge } from '#app/components/ProBadge/ProBadge.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useOnElementViewed } from '#app/hooks/useOnElementViewed.js';
import { ChevronRightIcon } from '@contra/icons/icons';
import { getRoute } from '@contra/routes';
import { useCallback } from 'react';
import { css, styled } from 'styled-components';

const StyledButtonLink = styled.a`
  ${({ theme }) => css`
    display: flex;
    padding: 16px 14px;
    width: 100%;
    justify-content: space-between;
    background-color: ${theme.colorsV2.gray05};
  `}
`;

const Gray80Text = styled(Text)`
  ${({ theme }) => css`
    color: ${theme.colorsV2.gray80};
    font-weight: 600;
    line-height: 16px;
  `}
`;

const Gray80BarChartIcon = styled.svg`
  color: ${({ theme }) => theme.colorsV2.gray80};
  flex-shrink: 0;
`;
const StyledRightChevronIcon = styled(ChevronRightIcon)`
  flex-shrink: 0;
`;

const InsightsRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;
const ProRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const SlimBarChartIcon = () => {
  return (
    <Gray80BarChartIcon
      fill="none"
      height="16"
      viewBox="0 0 16 16"
      width="16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        clipRule="evenodd"
        d="M8.5013 2.16602V2.66602V13.3327V13.8327H7.5013V13.3327V2.66602V2.16602H8.5013ZM13.8346 6.16602V6.66602V13.3327V13.8327H12.8346V13.3327V6.66602V6.16602H13.8346ZM3.16797 9.33268V8.83268H2.16797V9.33268V13.3327V13.8327H3.16797V13.3327V9.33268Z"
        fill="#373F51"
        fillRule="evenodd"
      />
    </Gray80BarChartIcon>
  );
};

export const MobileDiscoveryInsightsButton: React.FC = () => {
  const { track } = useAnalytics();
  const currentUser = useCurrentUser();

  const hasContraPro =
    currentUser?.profile?.hasActivePortfolioSubscription ?? false;

  const analyticsSectionSuffix = hasContraPro ? 'subscribed' : 'unsubscribed';

  const handleTrackView = useCallback(() => {
    track('button', {
      action: 'viewed',
      button_name: 'view_discovery_score',
      section: `profile_${analyticsSectionSuffix}`,
    });
  }, [analyticsSectionSuffix, track]);

  const handleCtaClick = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'view_discovery_score',
      section: `profile_${analyticsSectionSuffix}`,
    });
  }, [analyticsSectionSuffix, track]);

  const ref = useOnElementViewed<HTMLAnchorElement>(handleTrackView);

  return (
    <StyledButtonLink
      href={getRoute('/independent/discovery')}
      onClick={handleCtaClick}
      ref={ref}
      target="_blank"
    >
      <InsightsRow>
        <SlimBarChartIcon />
        <Gray80Text
          as="span"
          textStyle="subtitleSmall"
        >
          View profile insights
        </Gray80Text>{' '}
      </InsightsRow>
      <ProRow>
        <ProBadge size="xs" />
        <StyledRightChevronIcon size={16} />
      </ProRow>
    </StyledButtonLink>
  );
};
